import {Injectable} from '@angular/core';
import {HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest,} from '@angular/common/http';

import {EMPTY, Observable, throwError} from 'rxjs';
import {catchError, finalize, map, shareReplay, switchMap, timeout} from 'rxjs/operators';
import {Router} from '@angular/router';
import {AuthService} from '../../auth_modules/services/auth.service';
import {LocalStorageUtil} from '../utils/local-storage-util';
import {ObjectUtil} from '../utils/object-util';
import {AlertType} from '../common/models/alert';
import {CommonObservableService} from '../common/services/common-observable.service';

@Injectable({
  providedIn: 'root',
})
export class RequestInterceptor implements HttpInterceptor {
  private isRedirecting = false;
  private refreshInFlight$: Observable<string> | null = null;

  constructor(
    private router: Router,
    private authService: AuthService,
    private commonObservableService: CommonObservableService,
  ) {
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    let modifiedReq = req;

    const activeModule = this.getCurrentActiveModule();

    if (activeModule) {
      modifiedReq = modifiedReq.clone({
        setHeaders: {
          ModuleName: activeModule,
        },
      });
    }

    const token = LocalStorageUtil.getStorage().at;
    const isPublic = this.isPublicEndpoint(req);
    const isVersion = req.url.includes('/version');

    if (!ObjectUtil.isEmpty(token) && !isPublic) {
      modifiedReq = modifiedReq.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`,
        },
      });
    }

    if (!isVersion) {
      this.commonObservableService.startLoading();
    }

    return next.handle(modifiedReq).pipe(
      timeout(100000),

      catchError((error) => {
        if (error.name === 'TimeoutError') {
          console.error('Request timed out');
          return throwError(
            () => new Error('Request timeout, please try again.'),
          );
        }

        if (error instanceof HttpErrorResponse && error.status === 401) {
          const code = error?.error?.code;

          if (code === '5' || code == null) {
            if (!isPublic) {
              if (!this.authService.isUserActive()) {
                this.handleUnauthorized(error);
                return EMPTY;
              }

              console.log('REFRESH: got 401, trying refresh');
              return this.refreshToken$().pipe(
                catchError(() => {
                  this.handleUnauthorized(error);
                  return EMPTY;
                }),
                switchMap((newToken) =>
                  next.handle(this.withToken(modifiedReq, newToken)),
                ),
              );
            }

            if (!isVersion) {
              this.handleUnauthorized(error);
              return EMPTY;
            }
          }
        }

        return throwError(() => error);
      }),
      finalize(() => {
        if (!isVersion) {
          this.commonObservableService.stopLoading();
        }
      }),
    );
  }

  private handleUnauthorized(error: HttpErrorResponse): void {
    if (this.isRedirecting) return;

    this.isRedirecting = true;

    this.authService.clearLocalStorage();

    this.router
      .navigateByUrl('/auth/login', {
        state: {
          toast: {
            type: AlertType.ERROR,
            message:
              error?.error?.message ?? 'Session timeout. Please login again.',
          },
        },
      })
      .finally(() => {
        this.isRedirecting = false;
      });
  }

  private getCurrentActiveModule(): string | null {
    return sessionStorage.getItem('activeModule');
  }


  private refreshToken$(): Observable<string> {
    if (!this.refreshInFlight$) {
      this.refreshInFlight$ = this.authService.refreshWithOAuth().pipe(
        map((res) => {
          if (!res.access_token) {
            throw new Error('No access token in refresh response');
          }
          return res.access_token;
        }),
        shareReplay(1),
        finalize(() => {
          this.refreshInFlight$ = null;
        }),
      );
    }
    return this.refreshInFlight$;
  }

  private withToken(req: HttpRequest<any>, token: string): HttpRequest<any> {
    return req.clone({setHeaders: {Authorization: `Bearer ${token}`}});
  }

  private isPublicEndpoint(req: HttpRequest<any>): boolean {
    return req.url.includes('/oauth2/token') || req.url.includes('/version');
  }
}
